import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { Observable, ReplaySubject, of, throwError, timer } from 'rxjs';
import urlJoin from 'url-join';
import { v4 as uuidv4 } from 'uuid';
import { ICurrentUser, IUserSettings, IUserSettingsTab } from '../models';
import { BaseHttpService } from './base-http.service';
import { catchError, switchMap } from 'rxjs/operators';

/**
 * Manages user settings, preferences and current user state.
 * Singleton service — subscription to authenticationChanged intentionally lives for app lifetime.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseHttpService {

  // -------------------- Private state --------------------

  private settingsObject?: IUserSettings;
  private currentSettings: { [key: string]: any } = {};
  private lastSavedSettings: { [key: string]: any } = {};

  /** Prevents redundant loads on token renewal — reset on logout. */
  private hasLoaded: boolean = false;

  private _settingsTabs: IUserSettingsTab[] = [];
  private _currentUser$: ReplaySubject<ICurrentUser> = new ReplaySubject<ICurrentUser>(1);

  private settingsEndpoint: string;
  private currentUserEndpoint: string;

  // -------------------- Constructor --------------------

  constructor(
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,              
    protected oktaWrapper: OktaWrapperService,           
    impersonation: ImpersonationService                  
  ) {
    super(http, config, impersonation, oktaWrapper);

    const configUser = config['userSettings'];
    this.settingsEndpoint = configUser['settingsEndpoint'];
    this.currentUserEndpoint = configUser['currentUserEndpoint'];

    // Singleton service — subscription intentionally lives for app lifetime.
    // Handles: initial login, logout then re-login, return from Okta login page.
    // Does NOT reload on token renewal — hasLoaded guard prevents unnecessary API calls.
    this.oktaWrapper.authenticationChanged.subscribe((isAuth: boolean) => {

      if (isAuth && !this.hasLoaded) {
        // Genuine login or first authenticated load
        this.hasLoaded = true;
        this.load();
        this.updateLastLoginTime().subscribe({
          next: (response) => console.log('Last login time updated', response),
          error: (error) => console.error('Failed to update last login time', error)
        });

      } else if (!isAuth) {
        // Auth lost — reset state so next login triggers a fresh load
        this.hasLoaded = false;
        this.currentSettings = {};
        this.lastSavedSettings = {};
        this.settingsObject = undefined;
      }
    });

    // Handles browser reopen where 'added' never fires
    // but a valid token already exists in storage.
    if (this.oktaWrapper.isAuthenticatedSync()) {
      this.hasLoaded = true;
      this.load();
    }
  }

  // -------------------- Load --------------------

  /** Loads user settings and current user profile. */
  public load(): void {
    const token = this.getToken();
    if (token) {
      this.loadSettings(token);
      this.loadUser(token);
    }
  }

  /** Retrieves user settings from the API and populates local state. */
  private loadSettings(token: string): void {

    const url = urlJoin(this.baseUrl, this.settingsEndpoint);

    this.getItem<IUserSettings>(url, this.getHttpBearerHeaders(token))
      .subscribe({
        next: (prefs: any) => {
          if (prefs != null) {
            this.currentSettings = prefs.settings;
            this.lastSavedSettings = { ...this.currentSettings };
          }
        },
        error: (err) => console.error('Failed to load settings', err)
      });
  }

  /** Retrieves the current user profile and emits to subscribers. */
  private loadUser(token: string, attempt: number = 0): void {

    const url = urlJoin(this.baseUrl, this.currentUserEndpoint);

    this.getItem<ICurrentUser>(url, this.getHttpBearerHeaders(token))
      .subscribe({
        next: (user: ICurrentUser) => this._currentUser$.next(user),
        error: (err) => console.error('Failed to load user', err)
      });
  }

  // -------------------- Settings --------------------

  /** Returns the current in-memory settings object. */
  public getSettings(): { [key: string]: any } {

    return this.currentSettings;
  }

  /** Updates a single setting value in memory — call saveSettingsChanges() to persist. */
  public updateSetting(key: string, val: any): void {

    this.currentSettings[key] = val;
  }

  /** Persists current settings to the API and updates the last saved snapshot. */
  public saveSettingsChanges(): void {

    this.save();
    this.lastSavedSettings = { ...this.currentSettings };
  }

  /** Reverts in-memory settings to the last saved snapshot. */
  public cancelSettingsChanges(): void {

    this.currentSettings = { ...this.lastSavedSettings };
  }

  /** Persists current settings to the API. */
  private save(): void {

    const user = this.oktaWrapper.getUser();
    if (!user)
      return;

    const token = this.getToken();
    if (!token)
      return;

    if (!this.settingsObject) {
      this.settingsObject = {
        id: uuidv4(),
        userId: user.email,
        settings: {}
      };
    }

    this.settingsObject.settings = this.currentSettings;

    const url = urlJoin(this.baseUrl, this.settingsEndpoint);

    this.saveItem(this.settingsObject, true, url, this.getHttpBearerHeaders(token))
      .subscribe({
        next: (result) => console.log('Settings saved', result),
        error: (err) => console.error('Failed to save settings', err)
      });
  }

  // -------------------- User --------------------

  /** Returns an Observable of the current authenticated user profile. */
  public getCurrentUser(): Observable<ICurrentUser> {
    return this._currentUser$.asObservable();
  }

  /** Returns a blank user object for use as a safe default. */
  public get emptyUser(): ICurrentUser {

    return {
      systemId: '0',
      firstName: '',
      lastName: '',
      initials: '',
      fullName: '',
      alias: '',
      companyName: ''
    };
  }

  // -------------------- Settings tabs --------------------

  /** Registers the available settings tab definitions — called once from APP_INITIALIZER. */
  public registerSettingsTabs(items: IUserSettingsTab[]): void {

    this._settingsTabs = items;
  }

  /** Returns the registered settings tab definitions. */
  public getUserSettingsTabs(): IUserSettingsTab[] {

    return this._settingsTabs;
  }

  // -------------------- Last login --------------------

  /** Updates the last login timestamp for the current user. */
  public updateLastLoginTime(): Observable<any> {

    const token = this.getToken();
    const user = this.oktaWrapper.getUser();

    if (token && user) {
      const url = urlJoin(this.baseUrl, 'users/update-last-login');
      const body = {
        email: user.email,
        userId: user.uid,
        lastLoginTime: new Date().toISOString()
      };
      return this.http.post(url, body, { headers: this.getHttpBearerHeaders(token) });
    }

    return of(null);
  }
}
